// @ts-nocheck
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApi } from '../hooks/useApi'
import { useTwilioDevice } from '../hooks/useTwilioDevice'
import { useCallStore } from '../stores/callStore'
import CustomerCard from '../components/CustomerCard'
import { CustomerImportExport } from '../components/CustomerImportExport'
import { AdvancedSearchPanel } from '../components/AdvancedSearchPanel'
import { Users, Upload, Merge } from 'lucide-react'

export default function CustomersPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('list') // 'list' または 'import'
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [priorityFilter, setPriorityFilter] = useState('all')
  const [customerTypeFilter, setCustomerTypeFilter] = useState('all')
  const [advancedSearchConditions, setAdvancedSearchConditions] = useState({})
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })

  const api = useApi()
  const { makeOutboundCall } = useTwilioDevice()
  const { callStatus } = useCallStore()

  // 顧客一覧取得
  const fetchCustomers = async () => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      })

      if (searchQuery) {
        params.append('search', searchQuery)
      }
      if (priorityFilter !== 'all') {
        params.append('priority', priorityFilter)
      }
      if (customerTypeFilter !== 'all') {
        params.append('customerType', customerTypeFilter)
      }

      // Phase 5.4-B: 高度な検索条件を追加
      Object.entries(advancedSearchConditions).forEach(([key, value]) => {
        if (value) {
          params.append(key, value)
        }
      })

      const data = await api.get(`/api/customers?${params}`)
      setCustomers(data.customers || data.items || [])
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || data.count || 0,
        totalPages: data.pagination?.totalPages || 0
      }))
    } catch (err) {
      console.error('Failed to fetch customers:', err)
      setError(err.message || '顧客一覧の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers()
  }, [pagination.page, searchQuery, priorityFilter, customerTypeFilter, advancedSearchConditions])

  // フィルター変更時はページを1にリセット
  useEffect(() => {
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }))
    }
  }, [searchQuery, priorityFilter, customerTypeFilter, advancedSearchConditions])

  // Phase 5.4-B: 高度な検索ハンドラ
  const handleAdvancedSearch = (conditions) => {
    setAdvancedSearchConditions(conditions)
  }

  // ページ変更
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  // 顧客カードクリック
  const handleCustomerClick = (customer) => {
    navigate(`/customers/${customer.customerId}`)
  }

  // 顧客作成ボタン
  const handleCreateCustomer = () => {
    navigate('/customers/new')
  }

  // 顧客に発信
  const handleCallCustomer = async (customer) => {
    if (!customer.phoneNumber) {
      alert('電話番号が登録されていません')
      return
    }
    
    // 通話中チェック
    if (['connecting', 'connected'].includes(callStatus)) {
      alert('通話中は発信できません')
      return
    }
    
    try {
      await makeOutboundCall(customer.phoneNumber, {
        customerId: customer.customerId,
        customerName: customer.name,
      })
    } catch (err) {
      console.error('発信エラー:', err)
      alert(err.message || '発信に失敗しました')
    }
  }

  return (
    <div className="p-6">
      {/* ヘッダー */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">顧客管理</h1>
        
        {/* タブ */}
        <div className="flex space-x-1 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center ${
              activeTab === 'list'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="mr-2" size={16} />
            顧客一覧
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors flex items-center ${
              activeTab === 'import'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Upload className="mr-2" size={16} />
            インポート/エクスポート
          </button>
        </div>
      </div>

      {/* 顧客一覧タブ */}
      {activeTab === 'list' && (
        <>
          {/* 追加ボタン */}
          <div className="mb-4 flex justify-end gap-3">
            <button
              onClick={() => navigate('/customers/duplicates')}
              className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors flex items-center gap-2"
            >
              <Merge size={16} />
              重複顧客を検出
            </button>
            <button
              onClick={handleCreateCustomer}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              + 顧客を追加
            </button>
          </div>

          {/* 検索・フィルタ */}
          <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* 検索ボックス */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              検索
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="名前、電話番号、会社名で検索"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* 優先度フィルタ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              優先度
            </label>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">すべて</option>
              <option value="vip">VIP</option>
              <option value="high">高</option>
              <option value="normal">通常</option>
              <option value="low">低</option>
            </select>
          </div>

          {/* 顧客タイプフィルタ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              顧客タイプ
            </label>
            <select
              value={customerTypeFilter}
              onChange={(e) => setCustomerTypeFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">すべて</option>
              <option value="individual">個人</option>
              <option value="corporate">法人</option>
            </select>
          </div>
        </div>

        {/* クイックフィルタ */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            クイックフィルタ
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setPriorityFilter('all')
                setCustomerTypeFilter('all')
              }}
              className={`px-3 py-1 rounded-full text-sm ${
                priorityFilter === 'all' && customerTypeFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              すべて
            </button>
            <button
              onClick={() => {
                setPriorityFilter('vip')
                setCustomerTypeFilter('all')
              }}
              className={`px-3 py-1 rounded-full text-sm ${
                priorityFilter === 'vip'
                  ? 'bg-purple-600 text-white'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              VIP
            </button>
            <button
              onClick={() => {
                setPriorityFilter('all')
                setCustomerTypeFilter('corporate')
              }}
              className={`px-3 py-1 rounded-full text-sm ${
                customerTypeFilter === 'corporate'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              法人
            </button>
            <button
              onClick={() => {
                setPriorityFilter('all')
                setCustomerTypeFilter('individual')
              }}
              className={`px-3 py-1 rounded-full text-sm ${
                customerTypeFilter === 'individual'
                  ? 'bg-green-600 text-white'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              個人
            </button>
          </div>
          </div>
          </div>

          {/* Phase 5.4-B: 高度な検索パネル */}
          <div className="mb-6">
            <AdvancedSearchPanel onSearch={handleAdvancedSearch} />
          </div>

          {/* エラー表示 */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* ローディング表示 */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* 顧客一覧 */}
              {customers.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center">
                  <p className="text-gray-500">
                    {searchQuery || priorityFilter !== 'all' || customerTypeFilter !== 'all'
                      ? '条件に一致する顧客が見つかりませんでした'
                      : '顧客が登録されていません'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {customers.map((customer) => (
                    <CustomerCard
                      key={customer.customerId}
                      customer={customer}
                      onClick={() => handleCustomerClick(customer)}
                      onCall={handleCallCustomer}
                    />
                  ))}
                </div>
              )}

              {/* ページネーション */}
              {pagination.totalPages > 1 && (
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← 前へ
                    </button>
                    
                    <span className="text-gray-700">
                      ページ {pagination.page} / {pagination.totalPages}
                      <span className="text-gray-500 ml-2">
                        （全 {pagination.total} 件）
                      </span>
                    </span>
                    
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      次へ →
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* インポート/エクスポートタブ */}
      {activeTab === 'import' && (
        <CustomerImportExport />
      )}
    </div>
  )
}
